import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { MoreOutlined } from "us.icons";

import { RootState } from "us.helper/types";
import * as CommonActions from "us.common/actions";
import { $TableTree, $Popover, $Button, $Skeleton } from "us.common/components";

import { ItemMenu } from "./components";
import { DEFAULT_TABLE_INFO, ITEM_MENU_DEFAULT_STATE } from "./constants";
import { PostalAreasColumns } from "us.common/components/EntitySelection/constants";
import { clearText } from "us.common/components/EntitySelection/Functions";
import { PostalAreaActions } from "../../constants";

type ItemMenuState = {
  visible: boolean;
  id: number;
  visibleTooltip: boolean;
};

type PostalArea = {
  postalCodeId: number;
  postalCode: string;
  postalPlace: string;
  countyCode: string;
  countyName: string;
  countryCode: string;
  country: string;
  isUsed?: boolean;
};

type PostalAreasTableProps = {
  onAction: (action: PostalAreaActions, record: PostalArea) => void;
};

const PostalAreasTable: React.FC<PostalAreasTableProps & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();
  const { postalAreas, isPostalAreasLoading, onAction } = props;
  const [itemMenu, setItemMenu] = useState<ItemMenuState>(
    ITEM_MENU_DEFAULT_STATE
  );

  // Auto close item menu after 5 seconds
  const setAutoClose = useCallback((id: number) => {
    setTimeout(() => {
      setItemMenu((prev) => (prev.id === id ? ITEM_MENU_DEFAULT_STATE : prev));
    }, 5000);
  }, []);

  // Handle popover visibility
  const handlePopoverVisibility = (visible: boolean, record: PostalArea) => {
    if (visible) {
      setItemMenu({
        visible: true,
        id: record.postalCodeId,
        visibleTooltip: false,
      });
      setAutoClose(record.postalCodeId);
    } else {
      setItemMenu(ITEM_MENU_DEFAULT_STATE);
    }
  };

  const columns: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: 50,
      customRenderChild: (text: any, record: any, index: number) => {
        return (
          <$Popover
            placement="bottomLeft"
            trigger="click"
            visible={itemMenu.visible && itemMenu.id == record.postalCodeId}
            onVisibleChange={(visible: boolean) => {
              handlePopoverVisibility(visible, record);
            }}
            content={<ItemMenu onMenuClick={onAction} record={record} />}
            destroyTooltipOnHide
          >
            <$Button
              id="btnItemMenu"
              type="default"
              data-testid="popover-btn"
              icon={<MoreOutlined />}
              size="small"
            />
          </$Popover>
        );
      },
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.POSTALCODE"),
      dataIndex: "postalCode",
      key: "postalCode",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      customRenderChild: (text: any, record: any, index: number) => {
        const { postalCode, isUsed } = record;
        return <div>{postalCode}</div>;
      },
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.POSTALAREA"),
      dataIndex: "postalPlace",
      key: "postalPlace",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYCODE"),
      dataIndex: "countyCode",
      key: "countyCode",
      customFilter: true,
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYNAME"),
      dataIndex: "countyName",
      key: "countyName",
      customFilter: true,
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a.localeCompare(b),
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.COUNTRY"),
      dataIndex: PostalAreasColumns.COUNTRY,
      key: PostalAreasColumns.COUNTRY,
      className: "text-nowrap",
      customFilter: true,
      customSorter: (a: any, b: any) => a.localeCompare(b),
      filterSearch: true,
      onFilter: (value: string, { country }: any) =>
        clearText(country) == clearText(value),
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.STANDARD_CODE"),
      dataIndex: PostalAreasColumns.COUNTRY_CODE,
      key: PostalAreasColumns.COUNTRY_CODE,
      className: "text-nowrap",
      customFilter: true,
      customSorter: (a: any, b: any) => a.localeCompare(b),
      filterSearch: true,
      onFilter: (value: string, { countryCode }: any) =>
        clearText(countryCode) == clearText(value),
    },
  ];

  return (
    <div className="postal-areas-table mt-3">
      <$Skeleton loading={isPostalAreasLoading} active paragraph={{ rows: 5 }}>
        <$TableTree
          rowKey={(record: PostalArea) => record.postalCodeId}
          data={postalAreas}
          data-testid="postal-areas-table"
          size="small"
          filterOnType
          bordered
          onSort={(sortData, dataSource) => {
            return sortData(dataSource);
          }}
          onFilter={(searchData, dataSource) => {
            return searchData(dataSource);
          }}
          pagination={false}
          scroll={{ x: "100%", y: "calc(100vh - 160px)" }}
          columns={columns}
          locale={{
            filterConfirm: t("COMMON.OK"),
            filterReset: t("COMMON.RESET"),
          }}
          resetOnSourceChange={true}
          virtual={true}
        />
      </$Skeleton>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { postalAreas, isPostalAreasLoading } = state.entityDetails;
  return {
    postalAreas,
    isPostalAreasLoading,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PostalAreasTable);
